import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Simplified Workout Plan grid.
 * Days (Day One: Chest & Triceps, ...) each have a list of exercises with
 * Exercise / Protocol / Notes. Strength progression lives in the Strength tab.
 *
 * Trainer: inline edit, debounced auto-save. Client: read-only.
 */

const uid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());

const blankExercise = () => ({
  id: uid(),
  exercise: "",
  protocol: "",
  notes: "",
  is_tracked: false,  // kept for backward compat with stored data; not surfaced in UI
  weeks: [],          // kept for backward compat with stored data; not surfaced in UI
});

const blankDay = (label = "Day One") => ({
  id: uid(),
  day_label: label,
  focus: "",
  exercises: [blankExercise()],
});

export default function WorkoutGridTab({ clientId, user }) {
  const isTrainer = user.role === "trainer";
  const [grid, setGrid] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/workout-grid`);
        if (cancel) return;
        setGrid(data); setSavedAt(data.updated_at);
        if (data.days[0]) setActiveDay(data.days[0].id);
      } catch { toast.error("Failed to load workout plan"); }
    })();
    return () => { cancel = true; };
  }, [clientId]);

  useEffect(() => {
    if (!dirty || !isTrainer || !grid) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const body = { weeks_count: grid.weeks_count || 4, days: grid.days };
        const { data } = await api.put(`/clients/${clientId}/workout-grid`, body);
        setSavedAt(data.updated_at); setDirty(false);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
      finally { setSaving(false); }
    }, 1500);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line
  }, [grid, dirty]);

  const mutate = (fn) => {
    if (!isTrainer) return;
    setGrid((g) => { const n = structuredClone(g); fn(n); return n; });
    setDirty(true);
  };

  const addDay = () => mutate((g) => {
    const idx = g.days.length + 1;
    const label = `Day ${["One","Two","Three","Four","Five","Six","Seven"][idx - 1] || idx}`;
    const day = blankDay(label);
    g.days.push(day);
    setActiveDay(day.id);
  });

  const deleteDay = (dayId) => {
    if (!window.confirm("Delete this day?")) return;
    mutate((g) => {
      g.days = g.days.filter((d) => d.id !== dayId);
      if (activeDay === dayId) setActiveDay(g.days[0]?.id || null);
    });
  };

  const updateDay = (dayId, field, value) => mutate((g) => {
    const d = g.days.find((x) => x.id === dayId); if (d) d[field] = value;
  });

  const addExercise = (dayId) => mutate((g) => {
    const d = g.days.find((x) => x.id === dayId); if (!d) return;
    d.exercises.push(blankExercise());
  });

  const deleteExercise = (dayId, exId) => mutate((g) => {
    const d = g.days.find((x) => x.id === dayId); if (!d) return;
    d.exercises = d.exercises.filter((e) => e.id !== exId);
  });

  const updateExercise = (dayId, exId, field, value) => mutate((g) => {
    const ex = g.days.find((x) => x.id === dayId)?.exercises.find((e) => e.id === exId);
    if (ex) ex[field] = value;
  });

  if (!grid) return <div className="text-zinc-500 text-xs uppercase tracking-[0.3em]">Loading workout plan...</div>;
  const day = grid.days.find((d) => d.id === activeDay) || grid.days[0] || null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-3xl uppercase">Workout Plan</div>
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
            {grid.version ? `v${grid.version} · ` : ""}{savedAt ? `Updated ${new Date(savedAt).toLocaleString()}` : "Not set yet"}
          </div>
        </div>
        {isTrainer && (
          <span data-testid="workout-save-status" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            {saving ? "Saving..." : dirty ? "Unsaved" : savedAt ? "All saved" : ""}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-zinc-800" data-testid="workout-day-tabs">
        {grid.days.map((d) => (
          <button key={d.id} onClick={() => setActiveDay(d.id)}
            data-testid={`workout-day-tab-${d.id}`}
            className={`shrink-0 px-4 h-11 text-[11px] uppercase tracking-[0.2em] border ${activeDay === d.id ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"}`}>
            <div className="font-display text-base">{d.day_label}</div>
            <div className="text-[9px] tracking-[0.25em] opacity-80">{d.focus || "—"}</div>
          </button>
        ))}
        {isTrainer && (
          <Button onClick={addDay} data-testid="workout-add-day" className="shrink-0 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-[11px] uppercase tracking-[0.2em] h-11 px-4 border border-zinc-800">
            + Day
          </Button>
        )}
        {grid.days.length === 0 && !isTrainer && (
          <div className="text-zinc-600 text-sm py-3">No workout plan yet. Your coach will add it soon.</div>
        )}
      </div>

      {day && isTrainer && (
        <div className="flex flex-wrap items-end gap-3 border border-zinc-800 p-4">
          <Field label="Day Label">
            <Input data-testid="workout-day-label" value={day.day_label}
              onChange={(e) => updateDay(day.id, "day_label", e.target.value)}
              className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-9 w-36" />
          </Field>
          <Field label="Focus / Muscle Group">
            <Input data-testid="workout-day-focus" value={day.focus}
              onChange={(e) => updateDay(day.id, "focus", e.target.value)}
              placeholder="Chest & Triceps"
              className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-9 w-56" />
          </Field>
          <div className="ml-auto">
            <Button data-testid="workout-delete-day" onClick={() => deleteDay(day.id)} variant="outline" size="sm"
              className="rounded-none border-zinc-800 bg-transparent text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:bg-red-950 hover:text-red-300 hover:border-red-900">
              Delete Day
            </Button>
          </div>
        </div>
      )}

      {day ? (
        <div className="border border-zinc-800">
          <div className="hidden md:grid grid-cols-12 bg-[#121212] text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            <div className="col-span-3 p-3">Exercise</div>
            <div className="col-span-4 p-3 border-l border-zinc-800">Protocol</div>
            <div className="col-span-5 p-3 border-l border-zinc-800">Notes</div>
          </div>
          <div data-testid="workout-table">
            {day.exercises.map((ex) => (
              <div key={ex.id} className="border-t border-zinc-800 md:grid md:grid-cols-12 md:items-stretch relative group" data-testid={`workout-row-${ex.id}`}>
                <div className="md:col-span-3 p-2">
                  <Cell trainer={isTrainer} value={ex.exercise} placeholder="Seated Cable Flys"
                    onChange={(v) => updateExercise(day.id, ex.id, "exercise", v)}
                    testid={`workout-exercise-${ex.id}`} mono={false} />
                </div>
                <div className="md:col-span-4 p-2 md:border-l border-zinc-800">
                  <Cell trainer={isTrainer} value={ex.protocol} placeholder="1x15, 2x10, 1x18 (5 pause reps)"
                    onChange={(v) => updateExercise(day.id, ex.id, "protocol", v)}
                    testid={`workout-protocol-${ex.id}`} mono />
                </div>
                <div className="md:col-span-5 p-2 md:border-l border-zinc-800">
                  <Cell trainer={isTrainer} value={ex.notes} placeholder="Form cues, mind-muscle connection..."
                    onChange={(v) => updateExercise(day.id, ex.id, "notes", v)}
                    testid={`workout-notes-${ex.id}`} mono={false} rows={3} />
                </div>
                {isTrainer && (
                  <button onClick={() => deleteExercise(day.id, ex.id)}
                    data-testid={`workout-delete-ex-${ex.id}`}
                    title="Delete exercise"
                    className="absolute top-2 right-2 text-zinc-600 hover:text-[#FF3B30] text-xs w-7 h-7 grid place-items-center border border-zinc-800 hover:border-[#FF3B30] transition opacity-0 group-hover:opacity-100">
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          {isTrainer && (
            <div className="border-t border-zinc-800 p-3 bg-[#0A0A0A]">
              <Button onClick={() => addExercise(day.id)} data-testid="workout-add-exercise"
                variant="ghost"
                className="rounded-none text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white hover:bg-zinc-900">
                + Add Exercise
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-zinc-800 p-16 text-center text-zinc-500 text-sm">
          {isTrainer ? "Click + Day above to add the first training day." : "No workout plan yet."}
        </div>
      )}

      <div className="text-xs text-zinc-600 italic">
        Tracking strength progression? See the <span className="text-zinc-400">Strength</span> tab.
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">{label}</div>
    {children}
  </div>
);

function Cell({ trainer, value, onChange, placeholder, testid, mono, rows = 2 }) {
  const cls = `whitespace-pre-wrap leading-6 ${mono ? "font-mono text-xs" : "text-sm"}`;
  if (!trainer) {
    return value
      ? <div data-testid={testid} className={`${cls} text-zinc-100`}>{value}</div>
      : <div data-testid={testid} className="text-zinc-700 text-xs italic">—</div>;
  }
  return (
    <Textarea data-testid={testid} rows={rows} value={value || ""}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className={`rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white ${mono ? "font-mono text-xs" : "text-sm"} min-h-[60px]`} />
  );
}
